import React from 'react';
import { DialogActions, DialogContent, Grid2, Paper } from '@mui/material';
import { Formik, Form } from 'formik';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import FormikInput from '../../components/atom/FormikInput';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import ComponentTitle from '../../components/atom/ComponentTitle';
import { useTranslation } from 'react-i18next';
import { useResetPasswordRequest } from '@openapi/auth/auth';
import TikiForgotPasswordPage from './TikiForgotPasswordPage';

function ForgotPasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { notifyError, notifySuccess } = useNotify();
    const resetPasswordRequestMutation = useResetPasswordRequest({
        mutation: {
            onSuccess: () => {
                notifySuccess(t('login.forgotPasswordNotifySuccess'));
                navigate('/');
            },
            onError: notifyError,
        },
    });

    if (searchParams.get('theme') === 'tiki') {
        return <TikiForgotPasswordPage />;
    }

    const onSubmit = async (values, { setSubmitting }) => {
        resetPasswordRequestMutation.reset();
        try {
            await resetPasswordRequestMutation.mutateAsync({
                data: {
                    email: values.email,
                },
            });
        } catch (_error) {
            // onError already surfaced notification above
        } finally {
            setSubmitting(false);
        }
    };

    const validateSchema = Yup.object().shape({
        email: Yup.string().email(t('login.wrongEmailFormat')).required(t('common.required')),
    });

    return (
        <Grid2
            container
            justifyContent="center"
            sx={{ minHeight: '100vh', paddingTop: '10vh', px: { xs: 2, sm: 0 } }}
        >
            <Grid2 size={{ xs: 12, sm: 8, md: 6 }}>
                <Paper sx={{ p: 1 }}>
                    <Formik
                        initialValues={{ email: '' }}
                        validationSchema={validateSchema}
                        onSubmit={onSubmit}
                    >
                        {({ isSubmitting, dirty }) => (
                            <Form>
                                <DialogContent>
                                    <Grid2 container justifyContent="center" spacing={2}>
                                        <Grid2>
                                            <ComponentTitle
                                                variant="h4"
                                                title={t('login.forgotPasswordTitle')}
                                            />
                                        </Grid2>
                                        <FormikInput
                                            sm={12}
                                            label={t('login.email')}
                                            type="email"
                                            name="email"
                                        />
                                    </Grid2>
                                </DialogContent>
                                <DialogActions
                                    sx={{
                                        '@media (max-width:375px)': {
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            gap: 1,
                                            '& > :not(:first-of-type)': { ml: 0 },
                                        },
                                    }}
                                >
                                    <CommonButton
                                        onClick={() => {
                                            navigate('/');
                                        }}
                                        label={t('common.cancel')}
                                        buttonType="text"
                                    />
                                    <CommonButton
                                        disabled={
                                            isSubmitting ||
                                            !dirty ||
                                            resetPasswordRequestMutation.isPending
                                        }
                                        label={t('login.forgotPasswordButton')}
                                        buttonType="primary"
                                        type="submit"
                                    />
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </Paper>
            </Grid2>
        </Grid2>
    );
}

export default ForgotPasswordPage;
